import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { TimelineButtons } from '../components/TimelineButtons';
import { toast } from 'sonner';
import { format, parseISO, subDays, startOfDay } from 'date-fns';
import { 
    CalendarIcon, Moon, Sun, ChevronLeft, ChevronRight, 
    Trash2, Save, Clock, TrendingUp, Bed
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getQualityColor = (quality) => {
    if (quality >= 8) return 'bg-emerald-500';
    if (quality >= 6) return 'bg-sky-500';
    if (quality >= 4) return 'bg-amber-500';
    return 'bg-red-500';
};

const getQualityBgColor = (quality) => {
    if (quality >= 8) return 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800';
    if (quality >= 6) return 'bg-sky-50 border-sky-200 dark:bg-sky-950/30 dark:border-sky-800';
    if (quality >= 4) return 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800';
    return 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800';
};

export default function SleepTracker() {
    const { token } = useAuth();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [currentEntry, setCurrentEntry] = useState(null);
    const [bedtime, setBedtime] = useState('22:00');
    const [wakeTime, setWakeTime] = useState('07:00');
    const [sleepQuality, setSleepQuality] = useState(7);
    const [interruptions, setInterruptions] = useState(0);
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [stats, setStats] = useState(null);
    const [recentEntries, setRecentEntries] = useState([]);
    const [calendarOpen, setCalendarOpen] = useState(false);

    const fetchEntryForDate = useCallback(async (date) => {
        setLoading(true);
        try {
            const dateStr = format(date, 'yyyy-MM-dd');
            const res = await fetch(`${API_URL}/api/sleep/date/${dateStr}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (res.ok) {
                const data = await res.json();
                if (data) {
                    setCurrentEntry(data);
                    setBedtime(data.bedtime || '22:00');
                    setWakeTime(data.wake_time || '07:00');
                    setSleepQuality(data.sleep_quality || 7);
                    setInterruptions(data.interruptions || 0);
                    setNotes(data.notes || '');
                } else {
                    setCurrentEntry(null);
                    setBedtime('22:00');
                    setWakeTime('07:00');
                    setSleepQuality(7);
                    setInterruptions(0);
                    setNotes('');
                }
            }
        } catch (error) {
            console.error('Failed to fetch entry:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    const fetchStats = useCallback(async () => {
        try {
            const [statsRes, entriesRes] = await Promise.all([
                fetch(`${API_URL}/api/sleep/stats/summary?days=30`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${API_URL}/api/sleep?limit=7`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);
            
            if (statsRes.ok) {
                const statsData = await statsRes.json();
                setStats(statsData);
            }
            
            if (entriesRes.ok) {
                const entriesData = await entriesRes.json();
                setRecentEntries(entriesData);
            }
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        }
    }, [token]);

    useEffect(() => {
        fetchEntryForDate(selectedDate);
        fetchStats();
    }, [selectedDate, fetchEntryForDate, fetchStats]);

    const saveEntry = async () => {
        setSaving(true);
        try {
            const dateStr = format(selectedDate, 'yyyy-MM-dd');
            const res = await fetch(`${API_URL}/api/sleep`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    date: dateStr,
                    bedtime,
                    wake_time: wakeTime,
                    sleep_quality: sleepQuality,
                    interruptions,
                    notes: notes || null
                })
            });

            if (res.ok) {
                const data = await res.json();
                setCurrentEntry(data);
                toast.success('Sleep entry saved successfully');
                fetchStats();
            } else {
                throw new Error('Failed to save');
            }
        } catch (error) {
            toast.error('Failed to save sleep entry');
        } finally {
            setSaving(false);
        }
    };

    const deleteEntry = async () => {
        if (!currentEntry) return;
        if (!window.confirm('Are you sure you want to delete this entry?')) return;

        try {
            const res = await fetch(`${API_URL}/api/sleep/${currentEntry.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                setCurrentEntry(null);
                setBedtime('22:00');
                setWakeTime('07:00');
                setSleepQuality(7);
                setInterruptions(0);
                setNotes('');
                toast.success('Entry deleted');
                fetchStats();
            }
        } catch (error) {
            toast.error('Failed to delete entry');
        }
    };

    const goToPreviousDay = () => setSelectedDate(prev => subDays(prev, 1));
    const goToNextDay = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        if (selectedDate < startOfDay(tomorrow)) {
            setSelectedDate(prev => {
                const next = new Date(prev);
                next.setDate(next.getDate() + 1);
                return next;
            });
        }
    };

    const isToday = format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-bold">Sleep Tracker</h1>
                    <p className="text-muted-foreground mt-1">Track your sleep patterns and quality</p>
                </div>
                <TimelineButtons />
            </div>

            {stats && stats.total_entries > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/30 dark:to-violet-950/20 border-indigo-200 dark:border-indigo-800">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-indigo-500 rounded-lg">
                                    <Moon className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Avg Hours</p>
                                    <p className="text-xl font-bold">{stats.average_hours}h</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className={`border ${stats.average_quality ? getQualityBgColor(stats.average_quality) : ''}`}>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${stats.average_quality ? getQualityColor(stats.average_quality) : 'bg-gray-400'}`}>
                                    <TrendingUp className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Avg Quality</p>
                                    <p className="text-xl font-bold">{stats.average_quality}/10</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900/50 dark:to-slate-800/30">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-slate-500 rounded-lg">
                                    <Bed className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Entries (30d)</p>
                                    <p className="text-xl font-bold">{stats.total_entries}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {currentEntry && (
                        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/20">
                            <CardContent className="p-4">
                                <p className="text-xs text-muted-foreground">Last Night</p>
                                <p className="text-xl font-bold">{currentEntry.total_hours}h</p>
                                <p className="text-xs text-muted-foreground">{currentEntry.interruptions} wake-ups</p>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <Button variant="ghost" size="icon" onClick={goToPreviousDay} data-testid="prev-day-btn">
                                    <ChevronLeft className="w-5 h-5" />
                                </Button>
                                
                                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" className="min-w-[200px]" data-testid="date-picker-btn">
                                            <CalendarIcon className="w-4 h-4 mr-2" />
                                            {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="center">
                                        <Calendar
                                            mode="single"
                                            selected={selectedDate}
                                            onSelect={(date) => {
                                                if (date) {
                                                    setSelectedDate(date);
                                                    setCalendarOpen(false);
                                                }
                                            }}
                                            disabled={(date) => date > new Date()}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>

                                <Button variant="ghost" size="icon" onClick={goToNextDay} disabled={isToday} data-testid="next-day-btn">
                                    <ChevronRight className="w-5 h-5" />
                                </Button>
                            </div>
                            {isToday && <p className="text-center text-sm text-muted-foreground mt-2">Today</p>}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Log Sleep</CardTitle>
                            <CardDescription>
                                {currentEntry ? 'Update your sleep for this night' : 'Record your sleep for this night'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {loading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium mb-2 block">Bedtime</label>
                                            <div className="flex items-center gap-2">
                                                <Moon className="w-4 h-4 text-indigo-500" />
                                                <Input
                                                    type="time"
                                                    value={bedtime}
                                                    onChange={(e) => setBedtime(e.target.value)}
                                                    data-testid="bedtime-input"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium mb-2 block">Wake Time</label>
                                            <div className="flex items-center gap-2">
                                                <Sun className="w-4 h-4 text-amber-500" />
                                                <Input
                                                    type="time"
                                                    value={wakeTime}
                                                    onChange={(e) => setWakeTime(e.target.value)}
                                                    data-testid="waketime-input"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className={`p-4 rounded-xl border ${getQualityBgColor(sleepQuality)}`}>
                                        <label className="text-sm font-medium mb-3 block">Sleep Quality</label>
                                        <div className="flex items-center gap-4">
                                            <input
                                                type="range"
                                                min="1"
                                                max="10"
                                                value={sleepQuality}
                                                onChange={(e) => setSleepQuality(parseInt(e.target.value))}
                                                className="flex-1 h-3 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                                                data-testid="quality-slider"
                                            />
                                            <Badge className={`${getQualityColor(sleepQuality)} text-lg px-3 py-1`}>
                                                {sleepQuality}/10
                                            </Badge>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Night Interruptions</label>
                                        <div className="flex items-center gap-3">
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                onClick={() => setInterruptions(Math.max(0, interruptions - 1))}
                                                data-testid="interruptions-minus"
                                            >
                                                -
                                            </Button>
                                            <span className="text-2xl font-bold w-12 text-center">{interruptions}</span>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                onClick={() => setInterruptions(interruptions + 1)}
                                                data-testid="interruptions-plus"
                                            >
                                                +
                                            </Button>
                                            <span className="text-sm text-muted-foreground ml-2">times woken up</span>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Notes</label>
                                        <Textarea
                                            placeholder="Any notes about your sleep (dreams, restlessness, etc.)"
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            rows={3}
                                            data-testid="sleep-notes-input"
                                        />
                                    </div>

                                    <div className="flex gap-3 pt-2">
                                        <Button
                                            onClick={saveEntry}
                                            disabled={saving}
                                            className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                                            data-testid="save-sleep-btn"
                                        >
                                            <Save className="w-4 h-4 mr-2" />
                                            {saving ? 'Saving...' : currentEntry ? 'Update Entry' : 'Save Entry'}
                                        </Button>
                                        {currentEntry && (
                                            <Button variant="destructive" onClick={deleteEntry} data-testid="delete-entry-btn">
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Recent Sleep</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {recentEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground text-center py-4">
                                    No entries yet. Start tracking your sleep!
                                </p>
                            ) : (
                                <div className="space-y-3">
                                    {recentEntries.map((entry) => (
                                        <div
                                            key={entry.id}
                                            className={`p-3 rounded-lg border cursor-pointer hover:shadow-sm transition-shadow ${getQualityBgColor(entry.sleep_quality)}`}
                                            onClick={() => setSelectedDate(parseISO(entry.date))}
                                            data-testid={`recent-entry-${entry.date}`}
                                        >
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-sm font-medium">
                                                    {format(parseISO(entry.date), 'MMM d, yyyy')}
                                                </span>
                                                <Badge className={getQualityColor(entry.sleep_quality)}>
                                                    {entry.sleep_quality}/10
                                                </Badge>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                <Clock className="w-3 h-3" />
                                                <span>{entry.total_hours}h</span>
                                                <span>•</span>
                                                <span>{entry.interruptions} wake-ups</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/20 border-indigo-200 dark:border-indigo-800">
                        <CardHeader>
                            <CardTitle className="text-lg">💤 Sleep Tips</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm space-y-2 text-muted-foreground">
                            <p>• Keep a consistent sleep schedule</p>
                            <p>• Avoid screens 1 hour before bed</p>
                            <p>• Keep your room cool (65-68°F)</p>
                            <p>• Limit caffeine after 2pm</p>
                            <p>• Track to identify patterns</p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
